//* Libraries imports
import z from "zod";

//* Schemas imports
import { nameSchema } from "../../../name";
import { dateFormat } from "../../../date";


const collegeSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
});

const processSchema = z.object({
  id: z.number(),
  name: z.string(),
  start: dateFormat,
  end: dateFormat,
  description: z.string(),
  online: z.boolean(),
  simulation: z.number(),
  createdAt: dateFormat,
});

const testSchema = z.object({
  id: z.number(),
  name: z.string(),
  disciplines: z.array(z.object({
    id: z.number(),
    name: z.string(),
  })),  
});

export const paginatedResourceQuestion = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  deadline: dateFormat,
  candidateRegistrationNumber: z.string(),
  rationale: z.string().optional().nullable(),
  requestMade: z.string().optional().nullable(), 
  externalId: z.string().optional().nullable(),
  numberOfQuestions: z.number().or(z.string()).or(z.array(z.number())).optional().nullable(),
  createdAt: dateFormat,
  college: collegeSchema.optional().nullable(),
  process: processSchema.optional().nullable(),
});
export type PaginatedResourceQuestion = z.infer<typeof paginatedResourceQuestion>;

const fileSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  resourceId: z.string(),
  createdAt: dateFormat,
  updatedAt: dateFormat,
});

export const fullResourceQuestion = z
  .object({
    updatedAt: dateFormat,
    question: z.any().nullable(),
    categoria_id: z.number().or(z.string()).optional(),
    files: z.array(fileSchema),
    resourceResponses: z.array(z.any()).optional(),
    test: testSchema.optional().nullable(),
  })
  .merge(paginatedResourceQuestion);
export type FullResourceQuestion = z.infer<typeof fullResourceQuestion>;


export const createResourceQuestionSchema = z.object({
  name: nameSchema,
  description: z
    .string()
    .min(10, "Descrição deve ter pelo menos 10 caracteres"),
  faculdade_id: z.number().min(1, "Por favor, selecione uma faculdade válida"),
  processo_id: z
    .number()
    .min(1, "Por favor, selecione um processo seletivo válido"),
  prova_id: z
    .number()
    .min(1, "Por favor, selecione uma prova válida")
    .optional(),
  categoria_id: z
    .number()
    .min(1, "Por favor, selecione uma disciplina válida")
    .optional(),
  data_limite: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}$/,
      "Data limite deve estar no formato YYYY-MM-DD"
    ),
  numero_inscricao_candidato: z
    .string()
    .min(1, "Número de inscrição é obrigatório"),
  fundamentacao: z
    .string()
    .min(10, "Fundamentação deve ter pelo menos 10 caracteres"),
  pedido_realizado: z
    .string()
    .min(10, "Pedido realizado deve ter pelo menos 10 caracteres"),
  questao_id: z.number().min(1, "Por favor, selecione uma questão válida"),
  files: z
    .array(
      z.object({
        arquivo: z.string().min(1, "Arquivo é obrigatório"),
        tipo: z.enum(["image/jpeg", "image/png", "image/jpg"]),
      })
    )
    .max(10, "Máximo de 10 arquivos permitidos")
    .optional(),
});
export type CreateResourceQuestion = z.infer<typeof createResourceQuestionSchema>;

export const updateResourceQuestionSchema = createResourceQuestionSchema
  .partial()
  .omit({ processo_id: true })
  .extend({
    files_exists: z.array(z.string().min(1, "ID do arquivo deve ser válido")).max(10).optional(),
  })
  .refine(
    (data) => ((data.files?.length || 0) + (data.files_exists?.length || 0)) <= 10,
    {
      message: "Você pode ter no máximo 10 arquivos no total (novos + existentes)",
      path: ["files"],
    }
  );

export type UpdateResourceQuestion = z.infer<typeof updateResourceQuestionSchema>;

export const formResourceQuestionSchema = createResourceQuestionSchema
  .omit({ files: true })
  .extend({
    processo_id: z.number().min(1, "Por favor, selecione um processo seletivo válido").optional(),
    prova_id: z.number().min(1, "Por favor, selecione uma prova válida").optional(),
    categoria_id: z.number().min(1, "Por favor, selecione uma disciplina válida").optional(),
    files_exists: z.array(z.string()).optional(),
  });
  
export const formUpdateResourceQuestionSchema = formResourceQuestionSchema.partial();